import { ButtonList } from '@dixa/lib-shared-habla';
import { AsyncStatus, Message } from 'lib-shared-legacy-types';
import { memo, ReactNode, useCallback } from 'react';

import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import RecipientPill from '../../containers/messages/recipientPill';

import {
  isMessengerCustomFormMessage,
  MessageCustomForm,
} from './MessageCustomForm';
import TextMessage, { TextMessageProps } from './TextMessage';
import { DetailedErrorMessage } from './detailedErrorMessage';
import { useMessageFiles } from './utils/useMessageFiles';
import { useTranslatePlainTextConversationMessage } from './utils/useTranslatePlainTextConversationMessage';

type EmailMessageProps = {
  /** The message */
  message: Message;
  /** Callback for showing the original */
  onShowOriginal?: () => void;
  /** Callback for open print dialog */
  onFetchPrinter: () => void;
  /** Original email async status */
  originalEmailMeta: {
    fetchOriginalEmail: AsyncStatus;
    printerFriendly: AsyncStatus;
    htmlMessage: AsyncStatus;
  };
} & TextMessageProps;

export const EmailMessage = (props: EmailMessageProps): JSX.Element => {
  const {
    onFetchPrinter,
    onShowOriginal,
    originalEmailMeta,
    message,
    ...otherProps
  } = props;
  const { emailTranslation } = useFeatureFlag(['email-translation']);
  const { button, translation } = useTranslatePlainTextConversationMessage({
    text: message.text ?? undefined,
    isDisabled: !emailTranslation,
    originalLanguageCode: props.message.language ?? null,
  });

  const prepareMenu = useCallback(
    ({
      cc_list_v2,
      bcc_list_v2,
      to_list_v2,
      to,
      from,
    }: Message): JSX.Element | null => {
      const toArray = to ? [to] : [];
      const toList = Array.isArray(to_list_v2) ? to_list_v2 : toArray;
      const ccList = Array.isArray(cc_list_v2) ? cc_list_v2 : [];
      const bccList = Array.isArray(bcc_list_v2) ? bcc_list_v2 : [];

      if (toList.length || ccList.length || bccList.length) {
        const fromAttributeAsRecipient = {
          email: from ? from : null,
          name: null,
        };

        return (
          <RecipientPill
            id={`cc-bcc-pill-${message.id}`}
            from={fromAttributeAsRecipient}
            toList={toList}
            ccList={ccList}
            bccList={bccList}
          />
        );
      }
      return null;
    },
    [message.id],
  );

  const menu: ReactNode[] = [
    ...(typeof otherProps.failed === 'string'
      ? [
          <>
            <DetailedErrorMessage
              message={otherProps.failed}
              description={otherProps.failureReason}
            />
          </>,
        ]
      : []),
    ...(onShowOriginal
      ? [
          <>
            <ButtonList.Item
              icon="mail"
              isLoading={originalEmailMeta.fetchOriginalEmail === 'loading'}
              onClick={onShowOriginal}
            >
              View original email
            </ButtonList.Item>
            <ButtonList.Item
              icon="print"
              isLoading={originalEmailMeta.printerFriendly === 'loading'}
              onClick={onFetchPrinter}
            >
              Print email
            </ButtonList.Item>
          </>,
        ]
      : []),
  ];

  const { attachments, images } = useMessageFiles(
    message.inline_images,
    message.attached_files,
  );

  if (isMessengerCustomFormMessage(message)) {
    return <MessageCustomForm {...props} message={message} />;
  }

  return (
    <TextMessage
      {...otherProps}
      message={message}
      attachments={attachments}
      images={images}
      nameMenu={prepareMenu(message)}
      menu={menu}
      gutterButtons={button}
      translation={translation}
    />
  );
};

export default memo(EmailMessage);
